import _MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Tooltip } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { Feature, MultiPolygon, Point, Polygon } from '@turf/turf';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ControlPosition, useControl } from 'react-map-gl';
import { DRAW_CONTROL_STYLES } from './map.config';

type TDrawControlProps = ConstructorParameters<typeof _MapboxDraw>[0] & {
  position?: ControlPosition;
  onCreate?: (event: Feature<Point | Polygon | MultiPolygon>) => void;
  onUpdate?: (event: Feature<Point | Polygon | MultiPolygon>) => void;
  onDelete?: (event: Feature<Point | Polygon | MultiPolygon>) => void;
  onDrawControlInit?: (ref: MapboxDraw) => void;
  /** @default false - Hide drawing buttons (polygon/point) but keep edit/delete functionality */
  hideDrawingButtons?: boolean;
};

/**
 * Since the tooltip gets attached to the body, we need to use with styles to style it
 */
const StyledTooltip = withStyles(theme => ({
  tooltip: {
    background: theme.palette.common.white,
    borderRadius: 4,
    padding: theme.spacing(2),
    fontWeight: 500,
    boxShadow: '0 0 8px 4px rgba(0, 0, 0, 0.1)',
    color: theme.custom.themeColors.black,
  },
}))(Tooltip);

class MapboxDraw extends _MapboxDraw {
  _container: HTMLElement | undefined;
  constructor(props: TDrawControlProps) {
    super(props);
    const onAdd = this.onAdd;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.onAdd = (map: any) => {
      this._container = onAdd.call(this, map);
      return this._container;
    };
  }
}

const DrawControl: FC<TDrawControlProps> = ({
  onDrawControlInit,
  hideDrawingButtons,
  ...props
}) => {
  const drawControl = useControl(
    () =>
      new MapboxDraw({
        ...props,
        displayControlsDefault: false,
        styles: [...(props.styles || []), ...DRAW_CONTROL_STYLES],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any,
    ({ map }) => {
      /**
       * We only allow the creation of one feature at a time, therefor we can safely assume that the first feature in the array is the one that was created
       * This is the case for all draw modes
       */
      map.on('draw.create', ({ features }) => props.onCreate?.(features?.[0]));
      map.on('draw.update', ({ features }) => props.onUpdate?.(features?.[0]));
      map.on('draw.delete', ({ features }) => props.onDelete?.(features?.[0]));
    },
    ({ map }) => {
      /**
       * We only allow the creation of one feature at a time, therefor we can safely assume that the first feature in the array is the one that was created
       * This is the case for all draw modes
       */
      map.on('draw.create', ({ features }) => props.onCreate?.(features?.[0]));
      map.on('draw.update', ({ features }) => props.onUpdate?.(features?.[0]));
      map.on('draw.delete', ({ features }) => props.onDelete?.(features?.[0]));
    },
    {
      position: props.position,
    }
  ) as MapboxDraw;

  const [container, setContainer] = useState<HTMLElement | undefined>(undefined);

  const handleDeleteSelectedFeature = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        drawControl.trash();
      }
    },
    [drawControl]
  );

  useEffect(() => {
    onDrawControlInit?.(drawControl);
    //  Update container for the portal to draw on the buttons
    if (drawControl._container) {
      setContainer(drawControl._container);
    }

    document.addEventListener('keydown', handleDeleteSelectedFeature);
    return () => document.removeEventListener('keydown', handleDeleteSelectedFeature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawControl, handleDeleteSelectedFeature]);

  return container
    ? createPortal(
        <>
          {!hideDrawingButtons && (
            <>
              <StyledTooltip
                placement="right"
                title="Use the polygon drawing tool to draw polygons. Click on the map to mark points around the boundaries of a plot and click the first point again to close the polygon shape. There needs to be at least 3 points to form a polygon."
              >
                <button
                  // Default types is for some reason submit which triggers submitting the formik form
                  type="button"
                  className="mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_polygon"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    drawControl.changeMode('draw_polygon');
                  }}
                />
              </StyledTooltip>
              <StyledTooltip
                placement="right"
                title="Use the point drawing tool to draw single points. Click on the map to mark a new point and click again for the point to be saved."
              >
                <button
                  // Default types is for some reason submit which triggers submitting the formik form
                  type="button"
                  className="mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_point"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    drawControl.changeMode('draw_point');
                  }}
                />
              </StyledTooltip>
            </>
          )}
          <StyledTooltip
            placement="right"
            title="Once you have marked a polygon or point, click here to remove it."
          >
            <button
              // Default types is for some reason submit which triggers submitting the formik form
              type="button"
              className="mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_trash"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                drawControl.trash();
              }}
            />
          </StyledTooltip>
        </>,
        container
      )
    : null;
};

export default DrawControl;
